import type FastPhonicsReportingStudentDashboardTableRoute from 'teacher-ui-client/reporting/ui-scope/fast-phonics/dashboard/student/table/route'
import { ReportingTeacherUiHeader } from 'teacher-ui-client/components/reporting/teacher-ui-header'
import { ReportingStudentSummaryControls } from 'teacher-ui-client/components/reporting/student-summary-controls'
import { StudentDashboardTile } from 'teacher-ui-client/components/tiles/student-dashboard'
import { StudentDashboardProgressionTable } from 'teacher-ui-client/components/tables/student-dashboard/progression-table'
import { StudentDashboardActivityTable } from 'teacher-ui-client/components/tables/student-dashboard/activity-table'
import { StudentDashboardSingleRowTable } from 'teacher-ui-client/components/tables/student-dashboard/single-row-table'
import { t, type IntlService } from 'ember-intl'
import RouteTemplate from 'ember-route-template'
import { ThemeKeys } from 'teacher-ui-client/utils/themes'
import { ExportType } from 'teacher-ui-client/components/reporting/export-select'
import { array } from '@ember/helper'
import Component from '@glimmer/component'
import { formatValue } from 'teacher-ui-client/utils/format-value'
import { service } from '@ember/service'
import { FastPhonicsAssignmentsTile } from 'teacher-ui-client/components/tiles/student-dashboard/assignments/fast-phonics'
import type LessonInfoService from 'teacher-ui-client/services/lesson-info'
import { Precinct } from 'teacher-ui-client/utils/precincts'

/**
 * @deprecated - book names, not codes, should come from reporting data payload
 */
const fastPhonicsBooksData: Record<string, { name: string }> = {
  pat_taps: { name: 'Pat Taps' },
  tim_and_the_map: { name: 'Tim and the Map' },
  sam_can: { name: 'Sam can' },
  the_red_rocket: { name: 'The Red Rocket' },
  the_cat_and_the_rat: { name: 'The Cat and the Rat' },
  bill: { name: 'Bill' },
  kit_the_cat: { name: 'Kit the Cat' },
  carrots: { name: 'Carrots' },
  hot_dog: { name: 'Hot Dog' },
  peg_and_meg: { name: 'Peg and Meg' },
  ravis_pen: { name: "Ravi's Pen" },
  mud_on_the_rug: { name: 'Mud on the Rug' },
  pug_the_pup: { name: 'Pug the Pup' },
  off_on_a_jet: { name: 'Off on a Jet' },
  my_duck: { name: 'My Duck' },
  a_visit_to_the_vet: { name: 'A Visit to the Vet' },
  lots_of_pets: { name: 'Lots of Pets' },
  six_big_pigs: { name: 'Six Big Pigs' },
  fish_for_kit: { name: 'Fish for Kit' },
  bills_bath: { name: "Bill's Bath" },
  the_bug: { name: 'The Bug' },
  sunset: { name: 'Sunset' },
  king_rex: { name: 'King Rex' },
  can_a_chicken_get_mail: { name: 'Can a Chicken Get Mail?' },
  off_on_a_boat: { name: 'Off on a Boat' },
  max_gets_wet: { name: 'Max Gets Wet' },
  poor_kit: { name: 'Poor Kit' },
  a_visit_to_the_moon: { name: 'A Visit to the Moon' },
  charley_the_farm_dog: { name: 'Charley the Farm Dog' },
  sam_has_a_pain: { name: 'Sam Has a Pain' },
  dad_gets_mad: { name: 'Dad Gets Mad' },
  six_bad_chickens: { name: 'Six Bad Chickens' },
  kit_gets_lost: { name: 'Kit Gets Lost' },
  jess_the_jogger: { name: 'Jess the Jogger' },
  king_rexs_dinner: { name: "King Rex's Dinner" },
  a_trip_to_the_coast: { name: 'A Trip to the Coast' },
  the_test: { name: 'The test' },
  karl_and_charley_go_camping: { name: 'Karl and Charley Go Camping' },
  stinks_snack: { name: "Stink's Snack" },
  good_dog_scrap: { name: 'Good Dog, Scrap!' },
  gramps_the_grump: { name: 'Gramps the grump' },
  full_of_fleas: { name: 'Full of fleas!' },
  beach_clean_up: { name: 'Beach clean-up' },
  the_royal_pie: { name: 'The royal pie' },
  ways_to_get_about: { name: 'Ways to get About' },
  looking_for_humph: { name: 'Looking for Humph' },
  rain_rain_go_away: { name: 'Rain, rain go away' },
  news_reporters: { name: 'News Reporters' },
  the_new_stew: { name: 'The New Stew' },
  audrey_the_vet: { name: 'Audrey the Vet' },
  the_royal_golf_game: { name: 'The Royal Golf Game' },
  the_circus: { name: 'The Circus' },
  lost_in_the_cyber_place: { name: 'Lost in the Cyber Place' },
  ways_to_reduce_waste: { name: 'Ways to Reduce Waste' },
  going_on_a_plane: { name: 'Going on a Plane' },
  catnap_time: { name: 'Catnap Time' },
  a_knotty_problem: { name: 'A Knotty Problem' },
  please_call_the_plumber: { name: 'Please Call the Plumber' },
  shipwrecks: { name: 'Shipwrecks' },
}

interface Signature {
  Args: {
    model: ModelFor<FastPhonicsReportingStudentDashboardTableRoute>
  }
}

export class FpDashboardStudentTablePage extends Component<Signature> {
  @service intl!: IntlService

  @service lessonInfo!: LessonInfoService

  siteCopyKeyPrefix = 'reporting.fastPhonics.studentDashboard'

  pageTheme = ThemeKeys.blue

  get student() {
    return this.args.model.student
  }

  get pageTitle() {
    const studentName = this.student?.fullName || ''
    return this.intl.t(`reporting.fastPhonics.studentDashboard.pageTitle`, { name: studentName })
  }

  get peaksProgressionTableData() {
    const peaks = this.args.model.studentData?.lessons
    const { intl } = this
    return [
      { label: intl.t(`reporting.fastPhonics.studentDashboard.peaks.initialLesson`), value: peaks?.initial_lesson },
      {
        label: intl.t(`reporting.fastPhonics.studentDashboard.peaks.highestLesson`),
        value: peaks?.highest_lesson ?? undefined,
      },
      {
        label: intl.t(`reporting.fastPhonics.studentDashboard.peaks.totalLessons`),
        value: peaks?.total_lessons,
        isBold: true,
      },
      {
        label: intl.t(`reporting.fastPhonics.studentDashboard.peaks.totalLessonsMastered`),
        value: peaks?.mastered_lessons,
        isBold: true,
        isHighlighted: true,
        tooltip: intl.t(`reporting.fastPhonics.studentDashboard.peaks.totalLessonsMasteredTooltip`),
      },
    ]
  }

  get peaksPlacementActivityTableData() {
    const placementActivity = this.args.model.studentData?.lessons.placement_tests ?? []

    return placementActivity.map((activity) => {
      return this.buildPlacementActivityRow({
        activity,
        siteCopyKey: `reporting.fastPhonics.studentDashboard.peaks.placementText`,
      })
    })
  }

  get peaksActivityTableData() {
    const recentActivity = this.args.model.studentData?.lessons.recent_activity ?? []

    return recentActivity.map((activity) => {
      return this.buildActivityRow({
        activity,
        siteCopyKey: `reporting.fastPhonics.studentDashboard.peaks.activityText`,
      })
    })
  }

  get booksTableData() {
    const { intl } = this
    const { studentData } = this.args.model

    const booksRead = studentData?.books.books_read
    const wordsRead = studentData?.books.words_read
    const booksLabel = intl.t(`reporting.fastPhonics.studentDashboard.books.booksRead`)
    const wordsLabel = intl.t(`reporting.fastPhonics.studentDashboard.books.wordsRead`)
    return [
      { label: booksLabel, value: booksRead },
      { label: wordsLabel, value: wordsRead },
    ]
  }

  get booksActivityTableData() {
    const recentActivity = this.args.model.studentData?.books.recent_activity ?? []

    return recentActivity.map((activity) => this.buildBookActivityRow({ activity }))
  }

  get peaksProgressTableData() {
    const { intl } = this
    const { studentData } = this.args.model

    const sounds = studentData?.peaks_progress.sound_gems_earned
    const words = studentData?.peaks_progress.word_gems_earned
    const books = studentData?.peaks_progress.books_read

    const soundsLabel = intl.t(`reporting.fastPhonics.studentDashboard.peaksProgress.sounds`)
    const wordsLabel = intl.t(`reporting.fastPhonics.studentDashboard.peaksProgress.words`)
    const booksLabel = intl.t(`reporting.fastPhonics.studentDashboard.peaksProgress.books`)

    return [
      { label: soundsLabel, value: sounds },
      { label: wordsLabel, value: words },
      { label: booksLabel, value: books },
    ]
  }

  get peakQuizzesProgressionTableData() {
    const quizzes = this.args.model.studentData?.assessments

    const quizzesTakenLabel = this.intl.t(`reporting.fastPhonics.studentDashboard.peakQuizzes.numberQuizzesTaken`)
    const averageScoreLabel = this.intl.t(`reporting.fastPhonics.studentDashboard.peakQuizzes.averageQuizScore`)
    return [
      { label: quizzesTakenLabel, value: quizzes?.quizzes_taken },
      {
        label: averageScoreLabel,
        value: formatValue(quizzes?.average_score, { append: '%' }),
        isBold: true,
        isHighlighted: true,
      },
    ]
  }

  get peakQuizzesActivityTableData() {
    const recentActivity = this.args.model.studentData?.assessments.recent_activity ?? []

    return recentActivity.map((activity) => {
      return this.buildActivityRow({
        activity,
        siteCopyKey: `reporting.fastPhonics.studentDashboard.peakQuizzes.activityText`,
      })
    })
  }

  get areasOfWeaknessActivityTableData() {
    const recentActivity = this.args.model.studentData?.areas_of_weakness.recent_activity ?? []

    return recentActivity.map((activity) => {
      return this.buildActivityRow({
        activity,
        siteCopyKey: `reporting.fastPhonics.studentDashboard.areasOfWeakness.activityText`,
      })
    })
  }

  getLessonDataFor(lessonNumber: number) {
    return this.lessonInfo.lessonsFor(Precinct.FP_PEAKS).find((lesson) => lesson.lesson_number === lessonNumber)
  }

  buildActivityRow({
    activity,
    siteCopyKey,
  }: {
    activity: { created_at: number; lesson: number; score: number }
    siteCopyKey: string
  }) {
    const { created_at: createdAt, lesson: lessonNumber, score } = activity
    // Date
    const unixTimestampInMs = createdAt * 1000
    const date = new Date(unixTimestampInMs)
    // Label
    const fpLessonData = this.getLessonDataFor(lessonNumber)
    let label
    if (fpLessonData) {
      const { name, sounds } = fpLessonData
      label = this.intl.t(siteCopyKey, {
        lessonName: name,
        focusSounds: sounds?.join(', ') ?? '',
      })
    }
    // Value
    const value = formatValue(score, { append: '%' })

    return { date, label, value }
  }

  buildPlacementActivityRow({
    activity,
    siteCopyKey,
  }: {
    activity: { created_at: number; lesson: number; skipped_test: boolean }
    siteCopyKey: string
  }) {
    const { created_at: createdAt, lesson: lessonNumber, skipped_test: isSkipped } = activity
    // Date
    const unixTimestampInMs = createdAt * 1000
    const date = new Date(unixTimestampInMs)
    // Label
    const fpLessonData = this.getLessonDataFor(lessonNumber)
    let label
    if (fpLessonData) {
      const { name } = fpLessonData
      label = this.intl.t(siteCopyKey, {
        lessonName: name,
        isSkipped,
      })
    }
    // Value
    const value = ' '

    return { date, label, value }
  }

  buildBookActivityRow({ activity }: { activity: { created_at: number; book_code: string } }) {
    const { created_at: createdAt, book_code: bookCode } = activity
    // Date
    const unixTimestampInMs = createdAt * 1000
    const date = new Date(unixTimestampInMs)
    // Label
    // TODO book names, not codes, should come from reporting data payload
    const label = fastPhonicsBooksData[bookCode]?.name ?? ''
    const value = ' '

    return { date, label, value }
  }

  get assignmentsData() {
    const recentActivity = this.args.model.studentData?.assignments?.recent_activity ?? []

    return recentActivity.map((activity) => {
      return this.buildActivityRow({
        activity,
        siteCopyKey: `reporting.fastPhonics.studentDashboard.peaks.activityText`,
      })
    })
  }

  <template>
    <ReportingTeacherUiHeader @title={{this.pageTitle}} />

    <ReportingStudentSummaryControls
      @students={{@model.students}}
      @student={{@model.student}}
      @exportOptions={{array ExportType.print ExportType.CSV}}
      @isDataPresent={{Boolean @model.studentData}}
      @studentRouteName="reporting.ui-scope.fast-phonics.dashboard.student.table"
      @allStudentsRouteName="reporting.ui-scope.fast-phonics.dashboard.table"
    />

    <div class="mx-auto flex max-w-(--breakpoint-lg) flex-wrap print:block">
      <div class="w-full lg:w-1/2 print:w-full">
        <StudentDashboardTile
          data-test-student-dashboard-tile="peaks"
          class="m-3"
          @theme={{ThemeKeys.blue}}
          @title={{t "reporting.fastPhonics.studentDashboard.peaks.title"}}
          @icon="phosphor-share-network-fill"
        >
          <StudentDashboardProgressionTable
            exportable={{t "reporting.fastPhonics.studentDashboard.peaks.title"}}
            @theme={{ThemeKeys.blue}}
            @headerLabel={{t "reporting.fastPhonics.studentDashboard.peaks.progressionTitle"}}
            @tableData={{this.peaksProgressionTableData}}
          />
          <StudentDashboardActivityTable
            class="mt-3"
            exportable={{t "reporting.fastPhonics.studentDashboard.peaks.title"}}
            @theme={{ThemeKeys.blue}}
            @headerLabel={{t "reporting.fastPhonics.studentDashboard.peaks.placementTitle"}}
            @tableData={{this.peaksPlacementActivityTableData}}
          />
          <StudentDashboardActivityTable
            class="mt-3"
            exportable={{t "reporting.fastPhonics.studentDashboard.peaks.title"}}
            @theme={{ThemeKeys.blue}}
            @headerLabel={{t "reporting.fastPhonics.studentDashboard.peaks.activityTitle"}}
            @subheaderLabel={{t "reporting.fastPhonics.studentDashboard.peaks.activitySubtitle"}}
            @tableData={{this.peaksActivityTableData}}
          />
        </StudentDashboardTile>
        <StudentDashboardTile
          data-test-student-dashboard-tile="books"
          class="m-3"
          @theme={{ThemeKeys.blue}}
          @title={{t "reporting.fastPhonics.studentDashboard.books.title"}}
          @icon="phosphor-book-bookmark-fill"
        >
          <StudentDashboardSingleRowTable exportable @theme={{ThemeKeys.blue}} @tableData={{this.booksTableData}} />
          <StudentDashboardActivityTable
            class="mt-3"
            exportable={{t "reporting.fastPhonics.studentDashboard.books.title"}}
            @theme={{ThemeKeys.blue}}
            @headerLabel={{t "reporting.fastPhonics.studentDashboard.books.activityTitle"}}
            @tableData={{this.booksActivityTableData}}
          />
        </StudentDashboardTile>
      </div>
      <div class="w-full lg:w-1/2 print:w-full">
        <StudentDashboardTile
          data-test-student-dashboard-tile="peaks-progress"
          class="m-3"
          @theme={{ThemeKeys.blue}}
          @title={{t "reporting.fastPhonics.studentDashboard.peaksProgress.title"}}
          @icon="phosphor-share-network-fill"
        >
          <StudentDashboardSingleRowTable
            exportable={{t "reporting.fastPhonics.studentDashboard.peaksProgress.title"}}
            @theme={{ThemeKeys.blue}}
            @tableData={{this.peaksProgressTableData}}
          />
        </StudentDashboardTile>
        <StudentDashboardTile
          data-test-student-dashboard-tile="peak-quizzes"
          class="m-3"
          @theme={{ThemeKeys.blue}}
          @title={{t "reporting.fastPhonics.studentDashboard.peakQuizzes.title"}}
          @icon="phosphor-question-mark-bold"
        >
          <StudentDashboardProgressionTable
            exportable={{t "reporting.fastPhonics.studentDashboard.peakQuizzes.title"}}
            @theme={{ThemeKeys.blue}}
            @headerLabel={{t "reporting.fastPhonics.studentDashboard.peakQuizzes.progressionTitle"}}
            @tableData={{this.peakQuizzesProgressionTableData}}
          />
          <StudentDashboardActivityTable
            class="mt-3"
            exportable={{t "reporting.fastPhonics.studentDashboard.peakQuizzes.title"}}
            @theme={{ThemeKeys.blue}}
            @headerLabel={{t "reporting.fastPhonics.studentDashboard.peakQuizzes.activityTitle"}}
            @subheaderLabel={{t "reporting.fastPhonics.studentDashboard.peakQuizzes.activitySubtitle"}}
            @tableData={{this.peakQuizzesActivityTableData}}
          />
        </StudentDashboardTile>
        <StudentDashboardTile
          data-test-student-dashboard-tile="areas-of-weakness"
          class="m-3"
          @theme={{ThemeKeys.blue}}
          @title={{t "reporting.fastPhonics.studentDashboard.areasOfWeakness.title"}}
          @icon="phosphor-chart-line-bold"
        >
          <StudentDashboardActivityTable
            exportable={{t "reporting.fastPhonics.studentDashboard.areasOfWeakness.title"}}
            @theme={{ThemeKeys.blue}}
            @headerLabel={{t "reporting.fastPhonics.studentDashboard.areasOfWeakness.activityTitle"}}
            @subheaderLabel={{t "reporting.fastPhonics.studentDashboard.areasOfWeakness.activitySubtitle"}}
            @tableData={{this.areasOfWeaknessActivityTableData}}
          />
        </StudentDashboardTile>
        <FastPhonicsAssignmentsTile @data={{this.assignmentsData}} />
      </div>
    </div>
  </template>
}

export default RouteTemplate(FpDashboardStudentTablePage)
